import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, AppBar, Toolbar, Typography, IconButton, Breadcrumbs, Link, Box } from '@mui/material';
import { ArrowBack, Home } from '@mui/icons-material';

import HomePage from './components/HomePage';
import RadCalc from './components/RadCalc';
import HeadRoom from './components/HeadRoom';
import StairAngle from './components/StairAngle';
import FlareRad from './components/FlareRad';
import CircWalkway from './components/CircWalkway';
import CircRad from './components/CircRad';
import BalusterSpacing from './components/BalusterSpacing';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976D2', // Professional blue
      light: '#42A5F5',
      dark: '#0D47A1',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#37474F', // Professional blue-gray
      light: '#62727B',
      dark: '#263238',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#263238',
      secondary: '#546E7A',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.12)',
    '0px 8px 16px rgba(0,0,0,0.15)',
    '0px 12px 24px rgba(0,0,0,0.15)',
    '0px 16px 32px rgba(0,0,0,0.18)',
    '0px 20px 40px rgba(0,0,0,0.20)',
    '0px 24px 48px rgba(0,0,0,0.22)',
    '0px 28px 56px rgba(0,0,0,0.24)',
    '0px 32px 64px rgba(0,0,0,0.26)',
    '0px 36px 72px rgba(0,0,0,0.28)',
    '0px 40px 80px rgba(0,0,0,0.30)',
    '0px 44px 88px rgba(0,0,0,0.32)',
    '0px 48px 96px rgba(0,0,0,0.34)',
    '0px 52px 104px rgba(0,0,0,0.36)',
    '0px 56px 112px rgba(0,0,0,0.38)',
    '0px 60px 120px rgba(0,0,0,0.40)',
    '0px 64px 128px rgba(0,0,0,0.42)',
    '0px 68px 136px rgba(0,0,0,0.44)',
    '0px 72px 144px rgba(0,0,0,0.46)',
    '0px 76px 152px rgba(0,0,0,0.48)',
    '0px 80px 160px rgba(0,0,0,0.50)',
    '0px 84px 168px rgba(0,0,0,0.52)',
    '0px 88px 176px rgba(0,0,0,0.54)',
    '0px 92px 184px rgba(0,0,0,0.56)',
  ],
});

function NavigationBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const getPageTitle = (pathname: string) => {
    const routes: { [key: string]: string } = {
      '/radius-calc': 'Calculate Radius',
      '/headroom': 'Calculate HeadRoom',
      '/stair-angle': 'Calculate Stair Angle',
      '/flare-radius': 'Calculate Flare Radius',
      '/circular-walkway': 'Calculate Circular Walkway',
      '/circular-radius': 'Find Unknown Circular Radius',
      '/baluster-spacing': 'Calculate Baluster Spacing'
    };
    return routes[pathname] || 'Stair Math Calculator';
  };

  if (isHomePage) {
    return null; // Hide navigation bar on homepage
  }

  return (
    <AppBar position="static" elevation={2}>
      <Toolbar sx={{ minHeight: 64, py: 1 }}>
        <IconButton
          aria-label="Back to calculators"
          edge="start"
          color="inherit"
          onClick={() => navigate('/')}
          sx={{ 
            mr: { xs: 1, sm: 2 },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ 
              color: 'inherit',
              '& .MuiBreadcrumbs-separator': {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }}
          >
            <Link
              color="inherit"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <Home sx={{ mr: 0.5, fontSize: 18 }} />
              Home
            </Link>
            <Typography color="inherit" sx={{ fontWeight: 500 }}>
              {getPageTitle(location.pathname)}
            </Typography>
          </Breadcrumbs>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

function AppContent() {
  const location = useLocation();
  useEffect(() => {
    const heading = document.querySelector('h1');
    document.title = location.pathname === '/' ? 'Stair Math Calculator' : `${heading?.textContent || 'Page not found'} | Stair Math`;
    document.getElementById('main-content')?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return (
    <>
      <a className="skip-link" href="#main-content">Skip to calculator</a>
      <NavigationBar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/radius-calc" element={<RadCalc />} />
        <Route path="/headroom" element={<HeadRoom />} />
        <Route path="/stair-angle" element={<StairAngle />} />
        <Route path="/flare-radius" element={<FlareRad />} />
        <Route path="/circular-walkway" element={<CircWalkway />} />
        <Route path="/circular-radius" element={<CircRad />} />
        <Route path="/baluster-spacing" element={<BalusterSpacing />} />
        <Route path="*" element={
          <Box component="main" id="main-content" tabIndex={-1} sx={{ maxWidth: 600, mx: 'auto', px: 3, py: 8 }}>
            <Typography component="h1" variant="h4" gutterBottom>Page not found</Typography>
            <Typography sx={{ mb: 2 }}>Choose a calculator to continue.</Typography>
            <Link href="/">Back to calculators</Link>
          </Box>
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;